import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import {
  CREATE_ACCOUNT_PATH,
  LOGIN_PATH,
  REQUEST_PASSWORD_RESET_PATH,
  RESET_PASSWORD_PATH,
} from './values/values';
import { CanSeeOnlyWhenLoggedInGuard } from './guards/can-see-only-when-logged-in.guard';
import { CanSeeLoginPageGuard } from './guards/can-see-login-page.guard';
import { redirectAuthedToHomeGuard } from './guards/redirect-authed-to-home.guard';
import { demoEntryGuard } from './demo/demo-entry.guard';

const routes: Routes = [
  // The marketing landing lives at the base URL `/` for signed-out visitors;
  // authenticated users (and demo sessions) are bounced to /home by the guard.
  {
    path: '',
    pathMatch: 'full',
    title: 'Advocad — Measure your dev-advocacy reach',
    canActivate: [redirectAuthedToHomeGuard],
    loadComponent: () =>
      import('./landing/landing.component').then((m) => m.LandingComponent),
  },
  {
    path: LOGIN_PATH,
    title: 'Sign in — Advocad',
    canActivate: [CanSeeLoginPageGuard],
    loadChildren: () =>
      import('./login/login.module').then((module) => module.LoginModule),
  },
  {
    path: REQUEST_PASSWORD_RESET_PATH,
    loadChildren: () =>
      import('./forgot-password/forgot-password.module').then(
        (module) => module.ForgotPasswordModule
      ),
  },
  {
    path: RESET_PASSWORD_PATH,
    loadChildren: () =>
      import('./reset-password/reset-password.module').then(
        (module) => module.ResetPasswordModule
      ),
  },
  {
    path: CREATE_ACCOUNT_PATH,
    loadChildren: () =>
      import('./create-account/create-account.module').then(
        (module) => module.CreateAccountModule
      ),
  },
  // /welcome kept as a redirect to `/` so old links & bookmarks still resolve.
  { path: 'welcome', pathMatch: 'full', redirectTo: '' },
  // Dedicated get-started page: signup form + what-you-get preview.
  {
    path: 'signup',
    title: 'Get started — Advocad',
    canActivate: [CanSeeLoginPageGuard],
    loadComponent: () =>
      import('./signup/signup-page.component').then(
        (m) => m.SignupPageComponent,
      ),
  },
  // Public waitlist capture page (advocad.app/advocad.ai/waitlist) — the port
  // of the old GitHub Pages page so signups happen on our own domain.
  {
    path: 'waitlist',
    title: 'Join the waitlist — Advocad',
    loadComponent: () =>
      import('./waitlist/waitlist.component').then((m) => m.WaitlistComponent),
  },
  // Public interactive demo — an exact replica of the signed-in app on local
  // in-browser data: no login, nothing saved to the database (only the public
  // crawler is called). Used for launch walkthroughs (Product Hunt et al).
  {
    path: 'demo',
    title: 'Live demo — Advocad',
    canActivate: [demoEntryGuard],
    children: [],
  },
  // Self-hosted legal pages — public, linkable from anywhere.
  {
    path: 'terms',
    title: 'Terms of Service — Advocad',
    data: { doc: 'terms' },
    loadComponent: () =>
      import('./legal/legal.component').then((m) => m.LegalComponent),
  },
  {
    path: 'privacy',
    title: 'Privacy Policy — Advocad',
    data: { doc: 'privacy' },
    loadComponent: () =>
      import('./legal/legal.component').then((m) => m.LegalComponent),
  },
  // The authenticated app shell (home / activities / settings). Prefix-matches
  // everything not caught above; its guard sends signed-out users back to `/`.
  {
    path: '',
    canActivate: [CanSeeOnlyWhenLoggedInGuard],
    loadChildren: () =>
      import('./app-shell/app-shell.module').then(
        (module) => module.AppShellModule
      ),
  },
  // Unknown URLs → the root (landing for signed-out, /home for authed).
  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
